import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DepartmentTimesheetConfig, TimesheetCategory } from '@jira-clone/shared-types'
import { api } from '../../api/client'
import { useRefreshWidgets } from '../dashboard/api'
import type { TimesheetStatus } from '../../lib/timesheet'

// ── Shapes the timesheet endpoints return ─────────────────────────────────────

export interface TimesheetConfigResponse {
  department: string | null
  config: DepartmentTimesheetConfig
  channels: string[]
  workWeek: { workDays: number[]; hoursPerDay: number }
  timezone: string
  today: string
  roles: { canReview: boolean; canViewReports: boolean; canViewAll: boolean }
}

export interface TimesheetOptions {
  projects: Array<{ id: string; name: string }>
  leads: Array<{ id: string; name: string }>
  deals: Array<{ id: string; name: string }>
}

export interface TimesheetRowDto {
  id: string
  date: string
  durationMinutes: number
  category: TimesheetCategory
  activityType: string | null
  projectId: string | null
  projectName: string | null
  taskId: string | null
  taskTitle: string | null
  leadId: string | null
  leadName: string | null
  dealId: string | null
  dealName: string | null
  campaign: string | null
  channel: string | null
  deliverable: string | null
  revisionRound: number | null
  filingPeriod: string | null
  note: string | null
  source: 'manual' | 'copied' | 'call' | 'meeting' | 'task_worklog' | 'leave' | 'holiday'
  system: boolean
}

export interface TimesheetHistoryEntry {
  action: 'created' | 'submitted' | 'withdrawn' | 'approved' | 'rejected' | 'reopened' | 'edited'
  byName: string | null
  at: string
  reason: string | null
  totalBefore: number | null
  totalAfter: number | null
}

export interface TimesheetSheetDto {
  id: string
  userId: string
  userName: string | null
  userEmail: string | null
  department: string | null
  weekStartDate: string
  weekStart: string
  status: TimesheetStatus
  totalMinutes: number
  expectedMinutes: number | null
  notes: string | null
  submittedAt: string | null
  approvedAt: string | null
  rejectedAt: string | null
  rejectionReason: string | null
  history: TimesheetHistoryEntry[]
}

export interface WeekPayload {
  sheet: TimesheetSheetDto
  rows: TimesheetRowDto[]
  plan: Array<{ date: string; scheduled: number; expected: number; holiday: string | null; leave: { half: boolean } | null }>
  days: Array<{ date: string; scheduled: number; expected: number; worked: number; covered: number }>
  workedMinutes: number
  expectedMinutes: number
  warnings: string[]
  config: DepartmentTimesheetConfig
  permissions: { canEdit: boolean; canSubmit: boolean; canWithdraw: boolean; canReview: boolean; canReopen: boolean }
  submittedWarnings?: string[]
}

/** Time recorded elsewhere (calls, meetings, task time), offered as an entry to add. */
export interface SuggestionDto {
  ref: string
  source: 'call' | 'meeting' | 'task_worklog'
  label: string
  date: string
  durationMinutes: number
  activityType: string
  projectId: string | null
  projectName: string | null
  taskId: string | null
  taskTitle: string | null
  leadId: string | null
  leadName: string | null
  note: string
}

export interface AcceptResult extends WeekPayload {
  skipped: Array<{ ref: string; reason: string }>
}

export interface TimesheetListResponse {
  data: TimesheetSheetDto[]
  total: number
  limit: number
  offset: number
}

/** What a person fills in on the entry form. */
export interface RowFormValues {
  date: string
  durationMinutes: number
  category: TimesheetCategory
  activityType?: string | null
  projectId?: string | null
  taskId?: string | null
  leadId?: string | null
  dealId?: string | null
  campaign?: string | null
  channel?: string | null
  deliverable?: string | null
  revisionRound?: number | null
  filingPeriod?: string | null
  note?: string | null
}

const opts = { skipErrorToast: true }

// ── Reads ─────────────────────────────────────────────────────────────────────

export const useTimesheetConfig = () =>
  useQuery({
    queryKey: ['timesheets', 'config'],
    queryFn: () => api.get<TimesheetConfigResponse>('/timesheets/config', undefined, opts),
    staleTime: 5 * 60 * 1000,
  })

export const useTimesheetOptions = (enabled = true) =>
  useQuery({
    queryKey: ['timesheets', 'options'],
    enabled,
    queryFn: () => api.get<TimesheetOptions>('/timesheets/options', undefined, opts),
    staleTime: 2 * 60 * 1000,
  })

export const useProjectTasks = (projectId: string | null | undefined) =>
  useQuery({
    queryKey: ['timesheets', 'tasks', projectId],
    enabled: !!projectId,
    queryFn: () => api.get<{ tasks: Array<{ id: string; name: string }> }>('/timesheets/options/tasks', { projectId }, opts),
    staleTime: 60 * 1000,
  })

export const useWeek = (weekStart: string | null) =>
  useQuery({
    queryKey: ['timesheets', 'week', weekStart],
    enabled: !!weekStart,
    queryFn: () => api.get<WeekPayload>('/timesheets/week', { weekStart }, opts),
  })

export const useSheet = (id: string | null) =>
  useQuery({
    queryKey: ['timesheets', 'sheet', id],
    enabled: !!id,
    queryFn: () => api.get<WeekPayload>(`/timesheets/${id}`, undefined, opts),
  })

export const useSuggestions = (sheetId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ['timesheets', 'suggestions', sheetId],
    enabled: enabled && !!sheetId,
    queryFn: () => api.get<{ suggestions: SuggestionDto[] }>(`/timesheets/${sheetId}/suggestions`, undefined, opts),
  })

export const useTimesheetList = (scope: 'mine' | 'team' | 'all', status?: string, enabled = true) =>
  useQuery({
    queryKey: ['timesheets', 'list', scope, status ?? 'all'],
    enabled,
    queryFn: () => api.get<TimesheetListResponse>('/timesheets', { scope, status, limit: 52 }, opts),
  })

// ── Writes ────────────────────────────────────────────────────────────────────

/**
 * Everything that can be done to one sheet. Each call returns the sheet as it now is, which replaces
 * what is cached, so the screen updates without a second request; the lists and the dashboard
 * widgets are refreshed behind it.
 */
export function useSheetActions(sheetId: string | null | undefined, weekStart?: string | null) {
  const queryClient = useQueryClient()
  const refreshWidgets = useRefreshWidgets()

  const apply = (payload: WeekPayload) => {
    queryClient.setQueryData(['timesheets', 'sheet', payload.sheet.id], payload)
    queryClient.setQueryData(['timesheets', 'week', payload.sheet.weekStartDate], payload)
    if (weekStart && weekStart !== payload.sheet.weekStartDate) queryClient.invalidateQueries({ queryKey: ['timesheets', 'week', weekStart] })
    queryClient.invalidateQueries({ queryKey: ['timesheets', 'list'] })
    void refreshWidgets('timesheets', 'pending-timesheets', 'hr-kpis')
    return payload
  }

  const usePost = <T = unknown>(path: string, body?: unknown) =>
    useMutation({
      mutationFn: async (override?: T) => apply(await api.post<WeekPayload>(`/timesheets/${sheetId}${path}`, override ?? body, opts)),
    })

  return {
    addRow: useMutation({
      mutationFn: async (values: RowFormValues) => apply(await api.post<WeekPayload>(`/timesheets/${sheetId}/rows`, values, opts)),
    }),
    updateRow: useMutation({
      mutationFn: async ({ rowId, values }: { rowId: string; values: Partial<RowFormValues> }) =>
        apply(await api.patch<WeekPayload>(`/timesheets/${sheetId}/rows/${rowId}`, values, opts)),
    }),
    deleteRow: useMutation({
      mutationFn: async (rowId: string) => apply(await api.delete<WeekPayload>(`/timesheets/${sheetId}/rows/${rowId}`, opts)),
    }),
    saveNotes: useMutation({
      mutationFn: async (notes: string) => apply(await api.put<WeekPayload>(`/timesheets/${sheetId}/notes`, { notes }, opts)),
    }),
    acceptSuggestions: useMutation({
      mutationFn: async (refs: string[]) => {
        const result = await api.post<AcceptResult>(`/timesheets/${sheetId}/suggestions/accept`, { refs }, opts)
        apply(result)
        queryClient.invalidateQueries({ queryKey: ['timesheets', 'suggestions', sheetId] })
        return result
      },
    }),
    copyLastWeek: usePost('/copy-last-week'),
    submit: usePost('/submit'),
    withdraw: usePost('/withdraw'),
    approve: usePost('/approve'),
    reject: useMutation({
      mutationFn: async (reason: string) => apply(await api.post<WeekPayload>(`/timesheets/${sheetId}/reject`, { reason }, opts)),
    }),
    reopen: useMutation({
      mutationFn: async (reason: string) => apply(await api.post<WeekPayload>(`/timesheets/${sheetId}/reopen`, { reason }, opts)),
    }),
  }
}

// ── Reports (Super Admin) ─────────────────────────────────────────────────────

export interface HoursReport {
  from: string
  to: string
  status: 'all' | 'submitted_and_approved'
  totalMinutes: number
  expectedMinutes: number
  people: number
  byDepartment: Array<{ department: string; minutes: number; people: number }>
  byPerson: Array<{ userId: string; name: string; department: string | null; minutes: number; coverMinutes: number; expectedMinutes: number }>
  byProject: Array<{ projectId: string; name: string; minutes: number }>
  byActivity: Array<{ department: string; activity: string; minutes: number }>
  byCategory: Array<{ category: string; label: string; minutes: number }>
}

export interface ComplianceReport {
  weekStartDate: string
  totals: { people: number; submitted: number; approved: number; missing: number; onTime: number; submittedPercent: number }
  people: Array<{
    userId: string
    name: string
    department: string | null
    status: TimesheetStatus | 'none'
    workedMinutes: number
    expectedMinutes: number | null
    submittedAt: string | null
    onTime: boolean | null
    daysLate: number
  }>
}

export interface VarianceReport {
  from: string
  to: string
  people: Array<{
    userId: string
    name: string
    department: string | null
    timesheetMinutes: number
    attendanceMinutes: number
    differenceMinutes: number
    timesheetDays: number
    attendanceDays: number
    flag: 'ok' | 'over' | 'under' | 'no_attendance' | 'no_timesheet'
  }>
}

export const useHoursReport = (from: string, to: string, includeDrafts: boolean) =>
  useQuery({
    queryKey: ['timesheets', 'reports', 'hours', from, to, includeDrafts],
    queryFn: () => api.get<HoursReport>('/timesheet-reports/hours', { from, to, status: includeDrafts ? 'all' : undefined }, opts),
  })

export const useComplianceReport = (weekStart: string | null) =>
  useQuery({
    queryKey: ['timesheets', 'reports', 'compliance', weekStart],
    queryFn: () => api.get<ComplianceReport>('/timesheet-reports/compliance', { weekStart: weekStart ?? undefined }, opts),
  })

export const useVarianceReport = (from: string, to: string) =>
  useQuery({
    queryKey: ['timesheets', 'reports', 'variance', from, to],
    queryFn: () => api.get<VarianceReport>('/timesheet-reports/variance', { from, to }, opts),
  })

/** Downloads every entry in the range as a CSV file. */
export async function downloadTimesheetCsv(from: string, to: string, includeDrafts: boolean) {
  const blob = await api.get<Blob>('/timesheet-reports/export.csv', { from, to, status: includeDrafts ? 'all' : undefined }, { ...opts, responseType: 'blob' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `timesheets-${from}-to-${to}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
